import { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

/**
 * SearchableDropdown - type-to-search autocomplete, renders via portal to avoid overflow clipping
 */
const SearchableDropdown = ({
    value,
    onChange,
    options = [],
    placeholder = 'Select...',
    minWidth = 180,
    primaryColor = '#1c2266',
    variant = 'dark',
}) => {
    const isLight = variant === 'light';
    const [open, setOpen]       = useState(false);
    const [search, setSearch]   = useState('');
    const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
    const triggerRef            = useRef(null);
    const inputRef              = useRef(null);

    const selected    = options.find(o => String(o.value) === String(value));
    const isLargeList = options.length > 20;

    // Position the portal dropdown under the trigger
    const updatePos = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setDropPos({
                top:   rect.bottom + window.scrollY + 4,
                left:  rect.left   + window.scrollX,
                width: Math.max(rect.width, 220),
            });
        }
    };

    const handleOpen = () => {
        updatePos();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 30);
    };

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (triggerRef.current && !triggerRef.current.contains(e.target)) {
                const portal = document.getElementById('sd-portal');
                if (portal && portal.contains(e.target)) return;
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const filtered = search.trim()
        ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
        : options; // always show all, search just filters

    const handleSelect = (val) => {
        onChange(val);
        setOpen(false);
        setSearch('');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange('');
        setSearch('');
        setOpen(false);
    };

    const dropdownEl = open ? ReactDOM.createPortal(
        <div
            id="sd-portal"
            style={{
                position: 'absolute',
                top:      dropPos.top,
                left:     dropPos.left,
                width:    dropPos.width,
                zIndex:   99999,
                background: '#fff',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
            }}
        >
            {/* Search input */}
            <div style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9' }}>
                <input
                    ref={inputRef}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search..."
                    style={{
                        width: '100%', height: 28, border: '1px solid #e2e8f0',
                        borderRadius: 6, padding: '0 8px', fontSize: 12,
                        outline: 'none', boxSizing: 'border-box', color: '#1e293b',
                    }}
                />
            </div>
            {/* Options */}
            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                {filtered.length === 0 ? (
                    <div style={{ padding: '10px 12px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
                        No results{search ? ` for "${search}"` : ''}
                    </div>
                ) : filtered.map(o => (
                    <div
                        key={o.value}
                        onMouseDown={() => handleSelect(o.value)}
                        style={{
                            padding: '7px 12px', fontSize: 12, cursor: 'pointer',
                            background: String(o.value) === String(value) ? `${primaryColor}18` : 'transparent',
                            color: String(o.value) === String(value) ? primaryColor : '#1e293b',
                            fontWeight: String(o.value) === String(value) ? 600 : 400,
                            borderBottom: '1px solid #f8fafc',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}
                        onMouseEnter={e => { if (String(o.value) !== String(value)) e.currentTarget.style.background = `${primaryColor}10`; }}
                        onMouseLeave={e => { e.currentTarget.style.background = String(o.value) === String(value) ? `${primaryColor}18` : 'transparent'; }}
                    >
                        {o.label}
                    </div>
                ))}
            </div>
        </div>,
        document.body
    ) : null;

    return (
        <>
            <div
                ref={triggerRef}
                onClick={open ? () => { setOpen(false); setSearch(''); } : handleOpen}
                style={{
                    height: 29, display: 'flex', alignItems: 'center',
                    border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.3)',
                    borderRadius: isLight ? 4 : 8,
                    padding: '0 8px', fontSize: 12, cursor: 'pointer', gap: 4,
                    backgroundColor: isLight
                        ? (open ? '#f8fafc' : '#fff')
                        : (open ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)'),
                    color: isLight ? '#1e293b' : '#fff', minWidth, boxSizing: 'border-box', flexShrink: 0,
                    userSelect: 'none',
                }}
            >
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: selected ? 1 : 0.65 }}>
                    {selected ? selected.label : placeholder}
                </span>
                {selected && (
                    <span
                        onMouseDown={handleClear}
                        style={{ fontSize: 15, opacity: 0.7, cursor: 'pointer', lineHeight: 1, flexShrink: 0 }}
                    >×</span>
                )}
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', opacity: 0.7 }}>
                    <path d="M1 1l4 4 4-4" stroke={isLight ? '#64748b' : '#fff'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </div>
            {dropdownEl}
        </>
    );
};

export default SearchableDropdown;
